import React, { useContext, useState } from 'react';
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
//import 'bulma/css/bulma.min.css';

import { BeamlineDto, ProposalDto, } from './api/api.ts';
import { SelectingStatus } from './components/inputAutocomplete.tsx';
import { CurrentUserContext } from './currentUserProvider.tsx';
import { BeamlineAutocomplete, BeamlineSearchFunctions } from './components/beamlineAutocomplete.tsx';
import { ProposalAutocomplete, ProposalSearchFunctions } from './components/proposalAutocomplete.tsx';
import { MetadataContext } from './metadataProvider.tsx';
import './homePage.css';
import {timestampToTodayString, truthyJoin} from './utils.tsx';

enum SelectedTab { ForProposals, ForEsafs, ForUsers };


const HomePage: React.FC = () => {

  const currentUser = useContext(CurrentUserContext);
  const metaData = useContext(MetadataContext);
  const esafs = metaData.esafs;
  const beamlines = metaData.beamlines;

  const [beamlineValue, setBeamlineValue] = useState<BeamlineDto | null>(null)
  const [proposalValue, setProposalValue] = useState<ProposalDto | null>(null)
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<SelectedTab>(SelectedTab.ForUsers);

  const beamlineSearchFunctions:BeamlineSearchFunctions = {
    itemSelected: (item: BeamlineDto) => {
      setBeamlineValue(item);
      return SelectingStatus.Success
    },
    selectedNone: () => {
      setBeamlineValue(null);
      return SelectingStatus.Success
    }
  };

  const proposalSearchFunctions:ProposalSearchFunctions = {
    itemSelected: (item: ProposalDto) => {
      setProposalValue(item);
      return SelectingStatus.Success
    },
    selectedNone: () => {
      setProposalValue(null);
      return SelectingStatus.Success }
  };

  const beamlinesOwned = metaData.beamlines.slugsInDisplayOrder?.filter((slug) => {
    const beamline = metaData.beamlines.bySlug.get(slug)!;
    return beamline.ids_owners.includes(currentUser.userDetails?.id || -1);
  }).map((slug) => metaData.beamlines.bySlug.get(slug)!) || [];

  // If there are any archived ESAFs, we'll show a filtering checkbox to reveal them.
  const anyArchived = esafs.slugsInDisplayOrder?.some((slug) => {
    const esaf = metaData.esafs.bySlug.get(slug)!;
    return esaf.archived;
  });

  const filteredEsafs = esafs.slugsInDisplayOrder?.filter((slug) => {
    const esaf = metaData.esafs.bySlug.get(slug)!;
    if (proposalValue) {
      if (esaf.slug_proposal !== proposalValue.slug) {
        return false;
      }
    }
    if (beamlineValue) {
      if (esaf.slug_beamline !== beamlineValue.slug) {
        return false;
      }
    }
    if (!showArchived && esaf.archived) {
      return false;
    }
    return true;
  }) || [];

  // If any of the ESAFs that passed filtering are archived, we'll show an "Archived" column below.
  const anyArchivedFiltered = filteredEsafs.some((slug) => {
    const esaf = metaData.esafs.bySlug.get(slug)!;
    return esaf.archived;
  });

  const standardTabClasses = "inline-flex items-center justify-center py-2 px-6 font-medium border-gray-200 transition-all duration-300";
  const unselectedTabClasses = "text-gray-500 hover:text-black hover:bg-gray-100";
  const selectedTabClasses = "text-sky-900 bg-sky-300";

  return (

    <div className="bg-white grid grid-cols-2 shadow-lg divide-x divide-gray-400 h-full w-full rounded-lg">

      <div className="overflow-y-auto rounded-scrollbar" style={{padding: "1rem 1rem 0 1rem"}}>

        <div className="inline-flex items-center justify-center gap-3 mb-4">

          <div className="text-l font-semibold">
            Beamline:
          </div>

          <div>
            <BeamlineAutocomplete
                selectedItem={beamlineValue}
                searchFunctions={beamlineSearchFunctions}
                placeholder="Beamline to manage"
                greenWhenValid={false}
                canSelectNone={true}
                showClearButton={false}
              />
          </div>

        </div>

        <p className="mb-4">
          Contents of central storage for the selected beamline:
        </p>

        <img src={`${import.meta.env.BASE_URL}/fake-left.png`} style={{width: "350px"}} />

      </div>
      <div className="overflow-y-auto rounded-scrollbar" style={{padding: "1rem 1rem 0 1rem"}}>

        <p className="text-l font-semibold mb-4">
          Manage permissions for:
        </p>

        <div className="flex mb-4">
          <ul className="flex border border-gray-200 rounded-xl transition-all duration-300">
            <li>
              <a href="#" className={truthyJoin(standardTabClasses, "border-r rounded-l-xl", selectedTab == SelectedTab.ForProposals ? selectedTabClasses : unselectedTabClasses)}
                onClick={ () => setSelectedTab(SelectedTab.ForProposals) }
              >
                <svg className="w-4 h-4 me-1.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 3v4a1 1 0 0 1-1 1H5m8-2h3m-3 3h3m-4 3v6m4-3H8M19 4v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1ZM8 12v6h8v-6H8Z"/></svg>
                Proposals
              </a>
            </li>
            <li>
              <a href="#" className={truthyJoin(standardTabClasses, "border-r", selectedTab == SelectedTab.ForEsafs ? selectedTabClasses : unselectedTabClasses)}
                onClick={ () => setSelectedTab(SelectedTab.ForEsafs) }
              >
                <svg className="w-4 h-4 me-1.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.143 4H4.857A.857.857 0 0 0 4 4.857v4.286c0 .473.384.857.857.857h4.286A.857.857 0 0 0 10 9.143V4.857A.857.857 0 0 0 9.143 4Zm10 0h-4.286a.857.857 0 0 0-.857.857v4.286c0 .473.384.857.857.857h4.286A.857.857 0 0 0 20 9.143V4.857A.857.857 0 0 0 19.143 4Zm-10 10H4.857a.857.857 0 0 0-.857.857v4.286c0 .473.384.857.857.857h4.286a.857.857 0 0 0 .857-.857v-4.286A.857.857 0 0 0 9.143 14Zm10 0h-4.286a.857.857 0 0 0-.857.857v4.286c0 .473.384.857.857.857h4.286a.857.857 0 0 0 .857-.857v-4.286a.857.857 0 0 0-.857-.857Z"/></svg>
                ESAFs
              </a>
            </li>
            <li>
              <a href="#" className={truthyJoin(standardTabClasses, "rounded-r-xl", selectedTab == SelectedTab.ForUsers ? selectedTabClasses : unselectedTabClasses)}
                onClick={ () => setSelectedTab(SelectedTab.ForUsers) }
              >
                  <svg className="w-4 h-4 me-1.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0a8.949 8.949 0 0 0 4.951-1.488A3.987 3.987 0 0 0 13 16h-2a3.987 3.987 0 0 0-3.951 3.512A8.948 8.948 0 0 0 12 21Zm3-11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
                  Users
              </a>
            </li>
          </ul>
        </div>

        <img src={`${import.meta.env.BASE_URL}/fake-right.png`} style={{width: "350px"}} />

        <ProposalAutocomplete
            selectedItem={proposalValue}
            searchFunctions={proposalSearchFunctions}
            placeholder="Filter by Proposal"
            greenWhenValid={false}
            canSelectNone={true}
            showClearButton={true}
          />


      </div>
    </div>
  )
}

export default HomePage
